import { buildQuery } from '../../api/query';
import type { DiscoverySource } from '../../data/discovery';
import {
  readArray,
  readBoolean,
  readEnum,
  readMoney,
  readNumber,
  readRecord,
  readString,
} from '../readers';
import { semanticGet, semanticPost } from '../semanticRepository';
import type {
  RequestActivity,
  RequestActivityParams,
  RequestLevel,
  RequestLogParams,
  RequestLogRow,
  RequestLogsPage,
  RequestResult,
  RequestSnapshot,
  ResponseRetentionDays,
} from './model';

const LEVELS: readonly RequestLevel[] = ['success', 'error', 'rejected', 'pending'];
const AUTHS: readonly RequestLogRow['auth'][] = ['key', 'dashboard', 'oauth'];
const readLog = (value: unknown): RequestLogRow => {
  const row = readRecord(value, 'request log row');
  readEnum(row.level, LEVELS, 'request level');
  readEnum(row.auth, AUTHS, 'request authentication');
  readMoney(row.costUsd, 'request cost USD');
  return value as RequestLogRow;
};

export const readRequestLogs = (value: unknown): RequestLogsPage => {
  const row = readRecord(value, 'request logs page');
  return {
    requests: readArray(row.requests, 'request logs').map(readLog),
    nextCursor: row.nextCursor === null ? null : readNumber(row.nextCursor, 'request cursor'),
  };
};

const readFacetMoney = (value: unknown): void => {
  const row = readRecord(value, 'request activity facet');
  if (row.costUsd !== undefined) readMoney(row.costUsd, 'activity facet USD');
};

export const readRequestActivity = (value: unknown): RequestActivity => {
  const row = readRecord(value, 'request activity');
  readArray(row.buckets, 'activity buckets');
  readRecord(row.levels, 'request level counts');
  readArray(row.platforms, 'platform facets').forEach(readFacetMoney);
  readArray(row.errors, 'error facets');
  readMoney(row.totalCostUsd, 'total request cost USD');
  return value as RequestActivity;
};

export const fetchRequestLogs = (params: RequestLogParams, before?: number) =>
  semanticGet(`/v1/requests${buildQuery({ ...params, before })}`, readRequestLogs);
export const fetchRequestActivity = (params: RequestActivityParams) =>
  semanticGet(`/v1/requests/activity${buildQuery({ ...params })}`, readRequestActivity);

const DURABLE_STATUSES: readonly RequestSnapshot['status'][] = ['queued', 'running', 'succeeded', 'failed', 'expired'];
export const readRequestSnapshot = (value: unknown): RequestSnapshot => {
  const row = readRecord(value, 'request snapshot');
  const requestId = requiredText(row.requestId, 'durable request id');
  const sku = requiredText(row.sku, 'durable request SKU');
  const createdAt = readString(row.createdAt, 'durable request creation time');
  const completedAt = optionalText(row.completedAt, 'durable request completion time');
  const expiresAt = optionalText(row.expiresAt, 'durable request expiry time');
  const status = readEnum(row.status, DURABLE_STATUSES, 'durable request status');
  const base = {
    requestId,
    sku,
    createdAt,
    ...(completedAt === undefined ? {} : { completedAt }),
    ...(expiresAt === undefined ? {} : { expiresAt }),
  };

  if (status === 'queued' || status === 'running') {
    requireAbsent(row, ['result', 'error', 'resultExpired']);
    const retryAfterSeconds = readNumber(row.retryAfterSeconds, 'durable retry delay');
    if (!Number.isInteger(retryAfterSeconds) || retryAfterSeconds < 1) {
      throw new Error('Invalid durable retry delay');
    }
    return { ...base, status, retryAfterSeconds };
  }

  if (status === 'succeeded') {
    requireAbsent(row, ['error', 'retryAfterSeconds']);
    const resultExpired = row.resultExpired === undefined
      ? false
      : readBoolean(row.resultExpired, 'durable result expiry');
    if ((resultExpired && row.result !== undefined) || (!resultExpired && row.result === undefined)) {
      throw new Error('Invalid durable result');
    }
    return resultExpired
      ? { ...base, status, resultExpired: true }
      : { ...base, status, result: readRequestResult(row.result) };
  }

  if (status === 'failed') {
    requireAbsent(row, ['result', 'resultExpired', 'retryAfterSeconds']);
    const error = readRecord(row.error, 'durable request error');
    return { ...base, status, error: { code: requiredText(error.code, 'durable failure code') } };
  }

  requireAbsent(row, ['result', 'error', 'resultExpired', 'retryAfterSeconds']);
  return { ...base, status };
};

const requireAbsent = (row: Record<string, unknown>, fields: readonly string[]): void => {
  if (fields.some((field) => Object.prototype.hasOwnProperty.call(row, field))) {
    throw new Error('Invalid durable request state');
  }
};

export const readRequestResult = (value: unknown): RequestResult => {
  const row = readRecord(value, 'request result');
  if (!Object.prototype.hasOwnProperty.call(row, 'output') || row.provider !== 'AnyAPI') {
    throw new Error('Invalid request result');
  }
  const items = readNumber(row.items, 'request result items');
  if (!Number.isInteger(items) || items < 0) throw new Error('Invalid request result items');
  const source = readRequestSource(row.source);
  return {
    output: row.output,
    provider: 'AnyAPI' as const,
    costUsd: readMoney(row.costUsd, 'request result cost USD'),
    items,
    replayed: readBoolean(row.replayed, 'request result replay state'),
    ...(source === undefined ? {} : { source }),
  };
};

const SOURCE_KINDS: readonly DiscoverySource['kind'][] = ['anonymous', 'brand'];

/** Reads the optional source identity, dropping anything that is not the shape we
 *  publish. It never throws: the source decorates a result the customer already
 *  paid for, so a gateway that sends a malformed one must cost them the badge and
 *  not the run. */
const readRequestSource = (value: unknown): DiscoverySource | undefined => {
  if (value === undefined || value === null || typeof value !== 'object') return undefined;
  const row = value as Record<string, unknown>;
  const { id, name, kind, artworkKey } = row;
  if (typeof id !== 'string' || id === '') return undefined;
  if (typeof name !== 'string' || typeof artworkKey !== 'string') return undefined;
  if (!SOURCE_KINDS.includes(kind as DiscoverySource['kind'])) return undefined;
  return { id, name, kind: kind as DiscoverySource['kind'], artworkKey };
};

const requiredText = (value: unknown, label: string): string => {
  const text = readString(value, label);
  if (text.trim() === '') throw new Error(`Invalid ${label}`);
  return text;
};

const optionalText = (value: unknown, label: string): string | undefined =>
  value === undefined ? undefined : readString(value, label);

export const fetchRequestSnapshot = (id: string) =>
  semanticGet(`/v1/requests/${encodeURIComponent(id)}`, readRequestSnapshot);

const RETENTION_DAYS: readonly ResponseRetentionDays[] = [0, 3, 7];

const readResponseRetention = (value: unknown): ResponseRetentionDays => {
  const days = readNumber(readRecord(value, 'response retention').days, 'response retention days');
  if (!RETENTION_DAYS.includes(days as ResponseRetentionDays)) {
    throw new Error('Invalid response retention days');
  }
  return days as ResponseRetentionDays;
};

export const fetchResponseRetention = () =>
  semanticGet('/v1/settings/response-retention', readResponseRetention);

export const saveResponseRetention = (days: ResponseRetentionDays) =>
  semanticPost('/v1/settings/response-retention', { days }, readResponseRetention);
